
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { distinctUntilChanged, filter, take, takeUntil } from "rxjs/operators";
import { BroadcastKeys } from "src/app/common/enums/broadcast.enum";
import { MailBroadcaster } from "src/app/common/providers/mail-broadcaster.service";
import { CommonUtils } from "src/app/common/utils/common-util";
import { MailConstants } from "src/app/common/utils/mail-constants";
import { ConfigService } from "src/app/config.service";
import { environment } from "src/environments/environment";
import { MailService } from "../../services/mail-service";
import { DocumentPreviewComponent } from "@uxf/vnc-library";
import { Subject } from "rxjs";
import { Utils } from "src/app/common/utils";
import { FilesStorageService } from "src/app/services/files-storage.service";
import { ToastService } from "src/app/common/providers/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { DatabaseService } from "src/app/services/db/database.service";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { getProps, getUserProfile } from "src/app/reducers";
import { PreferenceState } from "src/app/preference/store/reducers";
import { AppState } from "src/app/reducers/app";
import { SearchState } from "src/app/reducers/search";
import { MailRootState } from "src/app/mail/store";
import { select, Store } from "@ngrx/store";

const TEXT_DOCUMENT_TYPES = ["doc", "docx", "doct", "odt", "gdoc", "txt", "rtf", "pdf", "mht", "htm", "html", "epub", "djvu", "xps", "oxps", "docm", "dot", "dotm", "dotx", "fodt", "ott", "fb2", "xml"];
const PRESENTATION_TYPES = ["pps", "ppsx", "ppt", "pptx", "odp", "pptt", "ppty", "gslides", "pot", "potm", "potx", "ppsm", "pptm", "fodp", "otp"];
const SPREADSHEET_TYPES = ["xls", "xlsx", "ods", "csv", "xlst", "xlsy", "gsheet", "xlsm", "xlt", "xltm", "xltx", "fods", "ots"];


@Component({
    selector: "vp-file-preview-dialog",
    templateUrl: "./file-preview-dialog.component.html",
    styleUrls: ["./file-preview-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewDialogComponent implements OnInit, OnDestroy {
    constructor(
        private dialogRef: MatDialogRef<FilePreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private changeDetectionRef: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        private config: ConfigService,
        private broadcaster: MailBroadcaster,
        private mailService: MailService,
        private toastService: ToastService,
        private dialog: MatDialog,
        private filesStorageService: FilesStorageService,
        private translate: TranslateService,
        private databaseService: DatabaseService,
        private store: Store<SearchState | AppState | MailRootState | PreferenceState>,

    ) {

        this.store.pipe(select(getProps), distinctUntilChanged()).subscribe(props => {
          console.log("getProps", props);
          if (!!props) {
            const officePreview = props.find(item => item.name === "officePreview");

            if (this.config.get("onlyOfficeApiUrl") && this.config.get("collaboraBaseUrl") && this.config.get("onlyOfficeApiUrl") !== "none" && this.config.get("collaboraBaseUrl") !== "none") {
              this.officeContent = (!!officePreview && !!officePreview._content) ? officePreview._content : "OFC";
            } else {
              if (this.config.get("onlyOfficeApiUrl") && this.config.get("onlyOfficeApiUrl") !== "none") {
                this.officeContent = "OFC";
              }
              if (this.config.get("collaboraBaseUrl") && this.config.get("collaboraBaseUrl") !== "none") {
                this.officeContent = "COLLAB";
              }
            }
          } else {
            if (this.config.get("onlyOfficeApiUrl") && this.config.get("onlyOfficeApiUrl") !== "none") {
              this.officeContent = "OFC";
            }
            if (this.config.get("collaboraBaseUrl") && this.config.get("collaboraBaseUrl") !== "none") {
              this.officeContent = "COLLAB";
            }
          }
        });
        this.onlyOfficeApiUrl = this.config.get("onlyOfficeApiUrl") || "none";
        this.officeConfig = Object.assign({}, this.officeConfig, {script: this.onlyOfficeApiUrl });
        if (this.data) {
            if (this.data && this.data.isDraft) {
              this.isDraft = this.data.isDraft;
            }
            if (this.data.msg) {
              this.msg = this.data.msg;
            }
            console.log("[FilePreviewDialogComponent] constructor data: ", this.data);
              this.attachment = this.data.attachment;

              if (this.attachment) {
                if (!!this.attachment && !!this.attachment.fileType) {
                  const ftype = this.attachment.fileType.toLowerCase();
                  console.log("[FilePreviewDialogComponent] constructor ftype: ", ftype);
                  if (ftype === "bmp") {
                    this.attachment.ct = "image/bmp";
                  }
                  if (ftype === "gif") {
                    this.attachment.ct = "image/gif";
                  }
                  if (ftype === "jpg") {
                    this.attachment.ct = "image/jpeg";
                  }
                  if (ftype === "jpeg") {
                    this.attachment.ct = "image/jpeg";
                  }
                  if (ftype === "png") {
                    this.attachment.ct = "image/png";
                  }
                  if (ftype === "pdf") {
                    this.attachment.ct = "application/pdf";
                  }
                  if (ftype === "webp") {
                    this.attachment.ct = "image/webp";
                  }
                  if (ftype === "md") {
                    this.attachment.ct = "text/x-web-markdown";
                  }
                }
                console.log("[FilePreviewDialogComponent] constructor: ", this.attachment);
                if (!!this.attachment) {
                    this.previewMail = {id: this.data?.msg?.id, part: this.attachment.part};
                    this.showOfficeViewer = this.isSupportedOffice(this.attachment?.ct) || this.data.isOffice;
                    this.isMailAttachment = this.checkMailAttachment(this.attachment);
                    this.isRenderableFile = this.isFileRenderable(this.attachment) || this.data.isOffice;
                    const contentType =  this.attachment.ct;
                    this.isPdfFile = this.isPdf(this.attachment);
                    if (this.isPdfFile && this.officeContent !== "OFC") {
                      // console.log("xxx filepreview officecontent: ", this.officeContent, this.isPdfFile);
                      this.previewWithCollabora(this.attachment, this.previewMail);
                    }
                    this.markdownFile = (contentType === "text/x-web-markdown" || contentType === "text/markdown") ? true : false;
                    if (contentType === "image/bmp" || contentType === "image/gif" || contentType === "image/jpeg" ||
                      contentType === "image/png" || contentType === "image/webp" ||
                       contentType === "video/mp4" ||
                      contentType === "video/x-flv" || contentType === "video/3gpp" || contentType === "video/quicktime" ||
                      contentType === "video/x-ms-wmv") {
                        this.isImageVideo = true;
                        console.log("[FilePreviewDialogComponent] constructor isImage : ", contentType);
                        if ( !CommonUtils.isOnMobileDevice() )  {
                                if ((this.isRenderableFile || this.isPdfFile || this.isMailAttachment) && !this.showOfficeViewer ) {
                                  this.dialogRef.close();
                                  this.openPreviewer();
                                  // this.setIframeImageFit();
                            }
                          }
                      }


                    let fType = this.getFileType();
                    let documentType = this.getDocumentType(fType);
                    if ( !!this.showOfficeViewer && (this.onlyOfficeApiUrl !== "none") ) {
                      // this.officeConfig = Object.assign({}, this.officeConfig, {
                      this.officeConfig = CommonUtils.rmap(this.officeConfig, {
                        editorConfig: {
                          documentType: documentType,
                          type: CommonUtils.isOnMobileDevice() ? "mobile" : "desktop",
                          document: {
                            fileType: this.getFileType(),
                            permissions: {
                              reader: true,
                              download: true,
                              edit: false
                            },
                            mode: "view",
                            title: this.attachment.filename,
                            url: this.attachment.url || this.getUrl(),
                            key: new Date().getTime().toString()
                          },
                          embedded: {
                            anonymous: false,
                            autosave: false
                          },
                          anonymous: false,
                          autosave: false,
                          lang: localStorage.getItem("mailLanguage") || "en"
                        }
                      });
                    }
                    console.log("[FilePreviewDialogComponent] constructor: ", this.officeConfig, this.attachment, this.isMailAttachment, this.isRenderableFile, this.isPdfFile, this.previewMail);
                    if (this.officeContent !== "OFC") {
                      // console.log("xxx filepreview officecontent2: ", this.officeContent);
                      this.previewWithCollabora(this.attachment, this.previewMail);
                    }
                    this.changeDetectionRef.markForCheck();
                }
            }
            this.changeDetectionRef.markForCheck();
            this.setUrl();
        }
        this.broadcaster.on(BroadcastKeys.HIDE_PREVIEW_DIALOG).pipe(take(1)).subscribe(() => {
            this.dialogRef.close();
        });
    }
    isDraft: boolean = false;
    attachment: any;
    msg: any;
    isRenderableFile: boolean = false;
    isPdfFile: boolean = false;
    markdownFile: boolean = false;
    displayFileContent: string = "";
    displayFileURL: any;
    PdfUrl: any;
    isMailAttachment: boolean = false;
    previewMail: { id: string; part: string; } =  { id: "", part: ""};
    onlyOfficeApiUrl: any;
    fullName: string = "";
    translations: any = {};
    officeConfig = {
      editorConfig: {
        document: {
          fileType: "docx",
          info: {
            author: "",
            created: "",
          },
          key: "",
          permissions: {
            reader: true,
            download: true,
            edit: false
          },
          title: "",
          url: "",
        },
        documentType: "text",
        editorConfig: {
          anonymous: true,
          autosave: false,
          lang: localStorage.getItem("mailLanguage") || "en",
          mode: "view",
          customization: {
            anonymous: {
              request: false,
              label: "Guest"
            },
          },
        },
        height: "100%",
        type: CommonUtils.isOnMobileDevice() ? "mobile" : "desktop",
        width: "100%",
      },
      script: "",
    };
  showOfficeViewer: boolean;
  private isAlive$ = new Subject<boolean>();
  openAgain: boolean = false;
  mobileDevice: boolean =  CommonUtils.isOnMobileDevice() ? true : false;
  isImageVideo: boolean = false;
  officeContent: string = "OFC";

    prevDocx: any = "";

    setIframeImageFit() {
        let dd: any = document.getElementsByClassName("preview-body");
        if (dd && dd.length) {
          dd[0].style.visibility = "hidden";
        }
        setTimeout(() => {
            let iframe: any = document.getElementsByTagName("iframe");
            if (!iframe) {
              dd[0].style.visibility = "visible";
            }
            if (iframe && iframe.length) {
              for (let i = 0 ; i < iframe.length; i++) {
                let iframeDocument = iframe[i].contentDocument || iframe[i].contentWindow.document;
                let iframeBody = iframeDocument.body;
                iframeBody.style.display = "flex";
                iframeBody.style["justify-content"] = "center";
                dd[0].style.visibility = "visible";
              }
            }
            if (!this.openAgain) {
              this.openAgain = true;
              this.setIframeImageFit();
            }



        }, 2000);

    }
    getFileType(): string {
      return (!!this.attachment.fileName) ? this.attachment.fileName.substr(this.attachment.fileName.lastIndexOf(".") + 1, this.attachment.fileName.length)  : this.attachment.filename.substr(this.attachment.filename.lastIndexOf(".") + 1, this.attachment.filename.length);
    }

    getDocumentType(fileType: string): string | undefined {
      fileType = fileType.toLowerCase();
      if (TEXT_DOCUMENT_TYPES.includes(fileType)) {
        return "word";
      } else if (SPREADSHEET_TYPES.includes(fileType)) {
        return "cell";
      } else if (PRESENTATION_TYPES.includes(fileType)) {
        return "slide";
      } else {
        return;
      }
    }

    ngOnInit() {
      if (Utils.isMobileDevice()) {
        setTimeout(() => {
          this.onIframeLoaded((iframeDocument) => {
            const elementInsideIframe = iframeDocument.getElementById("secondaryDownload");
            elementInsideIframe.addEventListener("click", () => {
              console.log("Secondary download button inside iframe clicked!", this.attachment, this.msg?.id);
              // this.downloadFile();
              let headers;
              if (CommonUtils.isOnIOS()) {
                  headers = [{
                      Key: "Authorization",
                      Value: localStorage.getItem("token")
                  }];
              } else {
                  headers = {"Authorization": localStorage.getItem("token")};
              }

              let queryParams = "id=" + this.msg?.id + "&part=" + this.attachment.part + "&disp=a";
              let url = CommonUtils.addTokenToRequest(this.config.API_URL + "/api/getAttachment/?" + queryParams);
              // Start
              this.filesStorageService.downloadAndSaveFileInBackground(url, headers, CommonUtils.isOnIOS(), this.attachment.filename).subscribe((localFileUrl) => {

                  this.toastService.show("FILE_DOWNLOADED");
                  if (CommonUtils.isOnIOS()) {
                    let mimeType = CommonUtils.getMediaType(localFileUrl);
                    if (CommonUtils.isOnIpad()) {
                      mimeType = CommonUtils.getMimeType(localFileUrl);
                      cordova.plugins.fileOpener2.open(
                        localFileUrl,
                        mimeType,
                        {
                            error : (e)  => {
                                console.error("[downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
                            },
                            success : () => {
                                console.info("[downloadFileAttachment] file opened successfully");
                            }
                        }
                    );
                    } else {
                      cordova.plugins.fileOpener2.showOpenWithDialog(
                        localFileUrl,
                        mimeType,
                        {
                            error : (e)  => {
                                console.error("[downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
                            },
                            success : () => {
                                console.info("[downloadFileAttachment] file opened successfully");
                            }
                        }
                      );
                    }
                  }
                  console.log("Downloaded success");

                }, err => {
                  console.log("[download recording][downloadInBackgroundFileAsBlob] err", err);
                    this.toastService.show("IMPORT_UNKNOWN_ERROR");
                });
              // End
            });
          });
        }, 100);
      }

    }

    onIframeLoaded(callback: (iframeDocument: Document) => void) {
      const iframe = document.getElementsByTagName("iframe")[0]; // Adjust index or selector as needed
      const checkIframeLoaded = setInterval(() => {
        if (iframe) {
          if (iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document)) {
            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            if (iframeDocument && iframeDocument.readyState === "complete") {
              clearInterval(checkIframeLoaded);
              callback(iframeDocument);
            }
          }
        }
      }, 100); // Check every 100 milliseconds
    }


    ngOnDestroy() { }

    close(): void {
        this.dialogRef.close();
    }

    checkMailAttachment(file): boolean {
        const contentType = file.ct;
        return contentType === "message/rfc822";
    }

    isSupportedOffice(contentType: string): boolean {
      return ((this.onlyOfficeApiUrl !== "none") && !!contentType && (contentType.startsWith("application/vnd.") || contentType === "application/msword"));
    }

    isFileRenderable(file): boolean {
        const contentType = file.ct;
        if (contentType === "application/pdf" && (CommonUtils.isOnMobileDevice() || environment.isElectron)) {
            return false;
        }
        if (this.isSupportedOffice(contentType) || contentType === "image/webp" ||
            contentType === "image/bmp" || contentType === "image/gif" || contentType === "image/jpeg" ||
            contentType === "image/png" || contentType === "text/html" || contentType === "text/plain" ||
            contentType === "application/pdf" || contentType === "application/x-zimbra-doc; charset=utf-8" ||
            contentType === "application/x-zimbra-doc" || contentType === "video/mp4" ||
            contentType === "video/x-flv" || contentType === "video/3gpp" || contentType === "video/quicktime" ||
            contentType === "video/x-ms-wmv" || contentType === "text/x-web-markdown" || contentType === "text/markdown") {
            return true;
        }
        return false;
    }

    isPdf(file): boolean {
      console.log("preview isPdf: ", file);
      if (!!file.fileType && !!file.url && file.url.startsWith("blob") && (file.fileType.toLowerCase() === "pdf")) {
        console.log("preview isPdf :  TRUE", file);
        return true;
      }
      const contentType = file.ct;
      if (contentType === "application/pdf") {
        console.log("isPdf true");
        return true;
      }
      return false;
    }

    downloadFile(): void {
        this.downloadAttachment(this.attachment, this.msg?.id);
    }

    getServerURL(): string {
        const isCordovaOrElectron = environment.isCordova || environment.isElectron;
        let serverURL = location.origin;
        if (isCordovaOrElectron) {
            serverURL = localStorage.getItem(MailConstants.SERVER_URL).trim();
        }
        return serverURL;
    }

    isBlobOrBase64Url(url) {
      return !!url ? (url.startsWith("blob") || url.startsWith("data:")) : false;
    }

    setUrl(): void {
        if (!!this.msg && !!this.attachment) {
            let queryParams = "id=" + this.msg?.id + "&part=" + this.attachment?.part;
            const url = (!!this.attachment.url && this.isBlobOrBase64Url(this.attachment.url)) ? this.attachment.url : this.getServerURL() + "/api/getAttachment/?" + queryParams;
            this.displayFileURL = (!!this.attachment.url && this.isBlobOrBase64Url(this.attachment.url)) ? this.sanitizer.bypassSecurityTrustResourceUrl(this.attachment.url) : this.sanitizer.bypassSecurityTrustResourceUrl(CommonUtils.addTokenToRequest(url));
            if (this.markdownFile && this.displayFileURL.changingThisBreaksApplicationSecurity) {
              this.mailService.fetchMarkdownFile(this.displayFileURL?.changingThisBreaksApplicationSecurity).subscribe(
                (data) => {
                  this.displayFileContent = data;
                  this.changeDetectionRef.markForCheck();
                },
                (error) => {
                  this.isRenderableFile = false;
                  this.markdownFile = false;
                }
              );
            }
            if (!!this.attachment.url && this.attachment.url.startsWith("data:application")) {
              const byteString = atob(this.attachment.url.split(",")[1]);
              let n = byteString.length;
              let u8arr = new Uint8Array(n);
              while (n--) {
                u8arr[n] = byteString.charCodeAt(n);
              }

              const blob = new Blob([u8arr]);
              this.PdfUrl = URL.createObjectURL(blob);
            } else {
              if (!!this.attachment.url && this.attachment.url.startsWith("blob:")) {
                this.PdfUrl = this.attachment.url;
              } else {
                this.PdfUrl = encodeURIComponent(CommonUtils.addTokenToRequest(url));
              }
            }
            console.log("preview displayFileURL: ", this.displayFileURL, url);
            console.log("preview PdfUrl: ", this.PdfUrl);
            this.changeDetectionRef.markForCheck();
            if (this.isRenderableFile && !!document.querySelector("iframe")) {
                setTimeout(() => {
                    const iframe = document.querySelector("iframe");
                    if (!!iframe && iframe !== null) {
                      const styles = "img { max-width: 100%; max-height: 100%;} body { text-align: center}";
                      const css: any = document.createElement("style");
                      css.type = "text/css";
                      css.appendChild(document.createTextNode(styles));
                      if (iframe.contentDocument.head !== null) {
                        iframe.contentDocument.head.appendChild(css);
                      }
                    }
                  }, 2000);
            }
        }
    }

    getUrl(): string {
      let queryParams = "id=" + this.msg?.id + "&part=" + this.attachment?.part;
      const url = this.getServerURL() + "/api/getAttachment/?" + queryParams;
      return CommonUtils.addTokenToRequest(url, true);
    }

    downloadAttachment(attachment: any, id: string): void {
      console.log("downloadAttachment", attachment, id);
      this.mailService.openAttachment(id, attachment.part, attachment);
    }

    private openSeprateDetailsWindow(attachment: any, id: string): void {
        window.open(
            window.location.origin + "/mail/launchNewDetailWindow/" + id + "/" + attachment.part,
            "",
            "toolbar=no,scrollbars=yes,resizable=yes,left=500,width=" + (window.innerWidth - 250) + ",height=600"
        );
    }


    createUrl() {
      let queryParams = "id=" + this.data.msg?.id + "&part=";
      const url = (!!this.attachment && !!this.attachment.url) ? this.attachment.url : this.getServerURL() + "/api/getAttachment/?" + queryParams;
      let basicUrl = (!!this.attachment && !!this.attachment.url) ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : this.sanitizer.bypassSecurityTrustResourceUrl(CommonUtils.addTokenToRequest(url));
      return url;
    }

    openPreviewer() {
      const triggerEvent = new Subject<any>();
      triggerEvent.pipe(takeUntil(this.isAlive$)).subscribe(v => {
        switch (v?.action) {
          case "download": {
            this.data.msg.attachmentList.map(attach => {
              if (attach.part === v.document.id) {
                this.downloadAttachment(attach, this.data.msg?.id);
              }
            });
            break;
          }
          case "copy": {
            this.data.msg.attachmentList.map(attach => {
              const url = v.document?.url;
              if (attach && url) {
                if (v.document?.filetype === "image") {
                  this.mailService.copyFileFromUrlToClipboard(url).subscribe({
                    next: () => this.toastService.show("FILE_COPY_TO_CLIPBOARD", 2000, "checkmark"),
                    error: () => this.toastService.show("COPY_FAILED", 3000, "close")
                  });
                } else {
                  url.searchParams?.delete("token");
                  const sanitizedUrl = url.toString();
                  CommonUtils.copyToClipboard([sanitizedUrl]);
                  this.toastService.show("FILE_COPY_TO_CLIPBOARD", 2000, "checkmark");
                }
              }
            });
            break;
          }
          case "task": {
            this.toastService.show("UNDER_DEVELOPMENT", 2000, "checkmark");
            break;
          }
          case "delete": {
            this.toastService.show("UNDER_DEVELOPMENT", 2000, "checkmark");
            break;
          }
          case "doubletap":
            if (v.document.type === "image") {
              this.mailService.triggerGallary.next("zoom");
            }
          break;
          default:
            break;
        }
      });

      let basicUrl = this.createUrl();
      let docList = [];
      console.log("openPreviewer this.data: ", this.data);
      this.fullName = this.getFullName(this.data?.msg);
      if (this.data.msg && this.data.msg.attachmentList) {
        this.data.msg.attachmentList.map(attach => {
          console.log("fileTypeData attach", attach);
          if ( attach.ct !== "text/plain" && !this.isPdf(attach)) {
            if ((this.checkMailAttachment(attach) || this.isFileRenderable(attach) || this.isPdf(attach)) && !this.isSupportedOffice(attach?.ct) ) {
              console.log("this.isVideoFileType(attach.ct)", this.isVideoFileType(attach.ct));
              let senderEmail: any;
              if (this.data?.msg?.e) {
                senderEmail = MailUtils.getEmailFromData(this.data?.msg);
              }
              let dataDoc = {
                // url: (basicUrl.startsWith("blob")) ? this.sanitizer.bypassSecurityTrustResourceUrl(basicUrl) : CommonUtils.addTokenToRequest(basicUrl + attach.part),
                avatarFallback : {email: senderEmail?.a, fullName: this.fullName},
                url: (basicUrl.startsWith("blob")) ? basicUrl : CommonUtils.addTokenToRequest(basicUrl + attach.part),
                filetype: this.isVideoFileType(attach.ct) ? "video" : "image",
                fileName: (!!attach.fileName) ? attach.fileName : attach.filename,
                id: attach.part,
                documentTypeImage: this.isPdf(this.data.attachment) ? false : true,
                timestamp: this.data.msg.d,
                avatarUrl: this.databaseService.avatar[senderEmail?.a] || senderEmail?.a,
                fullName: this.fullName,
              };
              if (this.isPdf(this.data.attachment)) {
                dataDoc.url = (basicUrl.startsWith("blob")) ? this.sanitizer.bypassSecurityTrustResourceUrl(basicUrl) : CommonUtils.addTokenToRequest(basicUrl + attach.part);
              }
              if (!this.isPdf(this.data.attachment)) {
                dataDoc["thumbnail_url"] = CommonUtils.addTokenToRequest(basicUrl + attach.part);
              }
              docList.push(dataDoc);
            }

          }
        });
      }

      const style: any = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
      const selectDoc = docList.find(doc => doc.id === this.data.attachment.part);
      let document = {
        // url: (basicUrl.startsWith("blob")) ? this.sanitizer.bypassSecurityTrustResourceUrl(basicUrl) : CommonUtils.addTokenToRequest(basicUrl + this.data.attachment.part),
        url: this.isBlobOrBase64Url(this.attachment.url) ? basicUrl : CommonUtils.addTokenToRequest(basicUrl + this.data.attachment.part),
        fileName: (!!this.data.attachment.fileName) ? this.data.attachment.fileName : this.data.attachment.filename,
        id: this.data.attachment.part,
        documentTypeImage: this.isPdf(this.data.attachment) ? false : true,
        filetype: this.isVideoFileType(this.data.attachment.ct) ? "video" : "image",
      };
      if (selectDoc) {
        document  = {...document , ...selectDoc};
      }
      let filesData = [];
      console.log("openPreviewer document1: ", document);
      if (this.isDraft) {
        if (document?.filetype === "image") {
          for ( const item of this.data.attachmentList ) {
            let file = {
              url: this.isBlobOrBase64Url(this.attachment.url) ? this.sanitizer.bypassSecurityTrustResourceUrl(basicUrl) : CommonUtils.addTokenToRequest(basicUrl + item.part),
              fileName: item.filename,
              id: item.part,
              documentTypeImage: this.isPdf(item) ? false : true,
              filetype: this.isVideoFileType(item.ct) ? "video" : "image",
            };
            if (!!item.ct) {
              const checkImage = item.ct?.split("/");
              if (file?.filetype === "image" && checkImage[0] === "image") {
                if (!this.isPdf(item)) {
                  filesData.push(file);
                }
              }
            } else {
              if (file?.filetype === "image") {
                if (!this.isPdf(item)) {
                  filesData.push(file);
                }
              }
            }
          }
        }
      } else {
        filesData = this.isPdf(this.data.attachment) ? [] : docList;
      }
      const translationsKey = ["CLOSE", "MORE_TEXT", "GALLERY_VIEW", "GRID_VIEW", "DELETE", "CREATE_VNCTASK", "EMAIL_TO_A_FRIEND", "ADD_TO_FAVORITES", "REMOVE_FROM_FAVORITES", "GOTO_SOURCE", "COPY_TO_CLIPBOARD", "IMAGE_COPY", "SHARE", "FORWARD", "DOWNLOAD_LBL", "COPY_TO_CLIPBOARD", "SEND_TO_OWNCLOUD"];
      let translations: any = {};
      this.translate.get(translationsKey).pipe(take(1)).subscribe(v => {
        translations = v;
        this.translations = v;
      });
      let headerAction = [
        {
          label: translations.DOWNLOAD_LBL,
          vncIconName: "download-new",
          action: "download"
        },
        {
          label: translations.COPY_TO_CLIPBOARD,
          vncIconName: "content-copy",
          action: "copy"
        }
      ];
      let contextMenuActions = [
        {
          label: translations.COPY_TO_CLIPBOARD,
          iconName: "mdi-content-copy",
          vncIconName: "content-copy",
          action: "copy"
        },
      ];
      const menuActions = [
        {
          label: translations.CREATE_VNCTASK,
          iconName: "mdi-content-copy",
          vncIconName: "task-new",
          action: "task"
        },
      ];
      if (this.isPdf(this.data.attachment)) {
        document.url = this.isBlobOrBase64Url(this.attachment.url) ? this.sanitizer.bypassSecurityTrustResourceUrl(basicUrl) : CommonUtils.addTokenToRequest(basicUrl + this.data.attachment.part);
      }
      if (!!this.attachment.url && this.isBlobOrBase64Url(this.attachment.url)) {
        const mappedFileData = {
          url: this.sanitizer.bypassSecurityTrustResourceUrl(this.attachment.url),
          thumbnail_url: this.attachment.url,
          fileName: document.fileName,
          id: document.id,
          documentTypeImage: this.isPdf(this.data.attachment) ? false : true,
          filetype: document.filetype,
        };
        filesData = [mappedFileData];
        // map filesData
        headerAction = [];
      }
      const disableCopyVideo = true;
      let previewDialog = this.dialog.open(DocumentPreviewComponent, Object.assign({
        backdropClass: "vnc-dialog-backdrop",
        panelClass: "vnc-preview-dialog-panel",
        disableClose: true,
        hasBackdrop: false,
        data: {
          isElectron: disableCopyVideo, // disabled copy video in all devices
          document: document,
          selectedId: document.id,
          viewerMode: "office",
          forMobile: false,
          files: filesData,
          menuActions: menuActions,
          contextMenuActions: contextMenuActions,
          showMoreOptionIcon : false,
          headerActions: headerAction,
          triggerEvent: triggerEvent,
          triggerGallary: this.mailService.triggerGallary,
          closeText: translations.CLOSE,
          imageZoomSlider: (this.isPdf(this.data.attachment) || this.isVideoFileType(this.data.attachment.ct)) ? false : true,
        },
        autoFocus: false
      }, style));

    }


  isVideoFileType(contentType) {
    if (contentType === "video/mp4" || contentType === "video/x-flv" || contentType === "video/3gpp" || contentType === "video/quicktime" || contentType === "video/x-ms-wmv") {
      return true;
    } else {
      return false;
    }
  }

  getFullName(message) {
    let info = "";
    if (message.e) {
      const emailInfo = MailUtils.getEmailFromData(message);
      if (!!emailInfo && (emailInfo.d || emailInfo.a || emailInfo.p)) {
        info = emailInfo.d || emailInfo.a;
        info = emailInfo.p || emailInfo.d || emailInfo.a.split("@")[0] || emailInfo.a;
        info.split("<").length > 1 ? info = info.split("<")[0].trim() : info = info;
      }
    }
    return info;
  }

    getExtention(fileName) {
      let extention = fileName?.split(".").pop();
      return extention;
    }
    previewWithCollabora(fileData: any= {}, message: any = {}) {
      let extention = this.getExtention(fileData.filename);
      console.log("[preview] 0", fileData, message);
      this.mailService.getPreviewWithCollabora({extension: extention, fileid: message.id  + "-" + fileData.part}).subscribe(res => {
          console.log("previewWithCollabora", res);
          this.prevDocx = this.sanitizer.bypassSecurityTrustResourceUrl(res.url);
          this.changeDetectionRef.markForCheck();
      });
    }

}
